type Mp4Box = {
  type: string
  payloadStart: number
  end: number
}

const CONTAINER_BOXES = new Set(['moov', 'trak', 'mdia'])

const fourCc = (bytes: Uint8Array, offset: number): string => String.fromCharCode(
  bytes[offset],
  bytes[offset + 1],
  bytes[offset + 2],
  bytes[offset + 3],
)

const readBox = (bytes: Uint8Array, offset: number, limit: number): Mp4Box | null => {
  if (offset + 8 > limit) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const size32 = view.getUint32(offset)
  const type = fourCc(bytes, offset + 4)
  let headerSize = 8
  let size = size32

  if (size32 === 1) {
    if (offset + 16 > limit) return null
    const high = view.getUint32(offset + 8)
    const low = view.getUint32(offset + 12)
    size = high * 0x1_0000_0000 + low
    headerSize = 16
  } else if (size32 === 0) {
    size = limit - offset
  }

  if (!Number.isSafeInteger(size) || size < headerSize || offset + size > limit) return null
  return { type, payloadStart: offset + headerSize, end: offset + size }
}

const collectHandlers = (
  bytes: Uint8Array,
  start: number,
  end: number,
  handlers: Set<string>,
): boolean => {
  let offset = start
  while (offset < end) {
    const box = readBox(bytes, offset, end)
    if (!box) return false
    if (box.type === 'hdlr') {
      if (box.payloadStart + 12 > box.end) return false
      handlers.add(fourCc(bytes, box.payloadStart + 8))
    } else if (CONTAINER_BOXES.has(box.type) && !collectHandlers(bytes, box.payloadStart, box.end, handlers)) {
      return false
    }
    offset = box.end
  }
  return offset === end
}

const readBlob = async (blob: Blob): Promise<ArrayBuffer> => {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.readAsArrayBuffer(blob)
  })
}

export async function inspectMp4AudioTrack(file: Blob): Promise<boolean | null> {
  try {
    const bytes = new Uint8Array(await readBlob(file))
    let offset = 0
    while (offset < bytes.length) {
      const box = readBox(bytes, offset, bytes.length)
      if (!box) return null
      if (box.type === 'moov') {
        const handlers = new Set<string>()
        if (!collectHandlers(bytes, box.payloadStart, box.end, handlers)) return null
        if (handlers.has('soun')) return true
        return handlers.has('vide') ? false : null
      }
      offset = box.end
    }
  } catch {
    return null
  }
  return null
}

const childBoxes = (bytes: Uint8Array, start: number, end: number): Mp4Box[] | null => {
  const boxes: Mp4Box[] = []
  let offset = start
  while (offset < end) {
    const box = readBox(bytes, offset, end)
    if (!box) return null
    boxes.push(box)
    offset = box.end
  }
  return boxes
}

const findBox = (bytes: Uint8Array, parent: Mp4Box, ...path: string[]): Mp4Box | null => {
  let current: Mp4Box | null = parent
  for (const type of path) {
    const children: Mp4Box[] | null = childBoxes(bytes, current.payloadStart, current.end)
    current = children?.find((box) => box.type === type) ?? null
    if (!current) return null
  }
  return current
}

export interface MediaSize {
  width: number
  height: number
}

export interface Mp4VideoInfo extends Partial<MediaSize> {
  fps: number
  frames: number
}

const trackSize = (bytes: Uint8Array, view: DataView, trak: Mp4Box): MediaSize | null => {
  const tkhd = findBox(bytes, trak, 'tkhd')
  if (!tkhd || tkhd.payloadStart + 4 > tkhd.end) return null
  const version = bytes[tkhd.payloadStart]
  if (version > 1) return null
  // version/flags, times + track id + reserved + duration, reserved/layer/group/volume, matrix; then 16.16 width/height.
  const sizeOffset = tkhd.payloadStart + 4 + (version === 1 ? 32 : 20) + 16 + 36
  if (sizeOffset + 8 > tkhd.end) return null
  const width = Math.round(view.getUint32(sizeOffset) / 0x1_0000)
  const height = Math.round(view.getUint32(sizeOffset + 4) / 0x1_0000)
  return width > 0 && height > 0 ? { width, height } : null
}

const trackInfo = (bytes: Uint8Array, view: DataView, mdia: Mp4Box): Mp4VideoInfo | null => {
  const mdhd = findBox(bytes, mdia, 'mdhd')
  const stts = findBox(bytes, mdia, 'minf', 'stbl', 'stts')
  if (!mdhd || !stts || mdhd.payloadStart + 4 > mdhd.end) return null
  const mdhdVersion = bytes[mdhd.payloadStart]
  if (mdhdVersion > 1) return null
  const timescaleOffset = mdhd.payloadStart + (mdhdVersion === 1 ? 20 : 12)
  if (timescaleOffset + 4 > mdhd.end || stts.payloadStart + 8 > stts.end) return null
  const timescale = view.getUint32(timescaleOffset)
  const entryCount = view.getUint32(stts.payloadStart + 4)
  if (stts.payloadStart + 8 + entryCount * 8 > stts.end) return null
  let samples = 0
  let duration = 0
  for (let index = 0; index < entryCount; index += 1) {
    const entry = stts.payloadStart + 8 + index * 8
    const count = view.getUint32(entry)
    samples += count
    duration += count * view.getUint32(entry + 4)
  }
  if (!timescale || !samples || !duration) return null
  return { fps: Number((samples * timescale / duration).toFixed(3)), frames: samples }
}

export async function inspectMp4VideoFps(file: Blob): Promise<number | null> {
  return (await inspectMp4Video(file))?.fps ?? null
}

export async function inspectMp4Video(file: Blob): Promise<Mp4VideoInfo | null> {
  try {
    const bytes = new Uint8Array(await readBlob(file))
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const moov = childBoxes(bytes, 0, bytes.length)?.find((box) => box.type === 'moov')
    if (!moov) return null
    for (const trak of childBoxes(bytes, moov.payloadStart, moov.end) ?? []) {
      if (trak.type !== 'trak') continue
      const mdia = findBox(bytes, trak, 'mdia')
      const hdlr = mdia && findBox(bytes, mdia, 'hdlr')
      if (!mdia || !hdlr || hdlr.payloadStart + 12 > hdlr.end) continue
      if (fourCc(bytes, hdlr.payloadStart + 8) === 'vide') {
        const info = trackInfo(bytes, view, mdia)
        return info ? { ...info, ...trackSize(bytes, view, trak) } : null
      }
    }
  } catch {
    return null
  }
  return null
}

export async function inspectMediaSize(file: Blob): Promise<MediaSize | null> {
  if (file.type.startsWith('image/')) {
    if (typeof createImageBitmap !== 'function') return null
    try {
      const bitmap = await createImageBitmap(file)
      const size = { width: bitmap.width, height: bitmap.height }
      bitmap.close()
      return size.width > 0 && size.height > 0 ? size : null
    } catch {
      return null
    }
  }
  const info = await inspectMp4Video(file)
  return info?.width && info.height ? { width: info.width, height: info.height } : null
}

export async function effectivePreserveAudio(file: Blob, requested: boolean): Promise<boolean> {
  if (!requested) return false
  return (await inspectMp4AudioTrack(file)) !== false
}
